// Auth/token layer for the desktop (authenticated) shell — DesktopApp.jsx.
// App.jsx (mobile shell) is device-ID-only and has no auth of its own.

export const API_BASE = (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) || "http://localhost:8000";

// Token storage
const LS = {
  get:    (k)    => { try { return localStorage.getItem(k); } catch { return null; } },
  set:    (k, v) => { try { localStorage.setItem(k, v); } catch {} },
  del:    (k)    => { try { localStorage.removeItem(k); } catch {} },
};

export function getStoredTokens() {
  return {
    accessToken:  LS.get("ftl_access_token"),
    refreshToken: LS.get("ftl_refresh_token"),
    userId:       LS.get("ftl_user_id"),
    email:        LS.get("ftl_email"),
  };
}
export function storeTokens({ access_token, refresh_token, user_id, email }) {
  LS.set("ftl_access_token",  access_token);
  LS.set("ftl_refresh_token", refresh_token);
  LS.set("ftl_user_id",       user_id);
  LS.set("ftl_email",         email);
}
export function clearTokens() {
  ["ftl_access_token","ftl_refresh_token","ftl_user_id","ftl_email"].forEach((k) => LS.del(k));
}

// Bare fetch — no auth header
export async function rawFetch(path, opts = {}) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { "Content-Type": "application/json", ...(opts.headers ?? {}) },
      ...opts,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail ?? `HTTP ${res.status}`);
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
}

// Auth-aware fetch — attaches Bearer token, auto-refreshes on 401
let _refreshPromise = null; // dedupe concurrent refresh attempts

export async function apiFetch(path, opts = {}, onTokenRefresh) {
  const { accessToken } = getStoredTokens();
  const headers = {
    "Content-Type": "application/json",
    ...(opts.headers ?? {}),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };

  try {
    let res = await fetch(`${API_BASE}${path}`, { ...opts, headers });

    if (res.status === 401) {
      // Single refresh in flight — all concurrent 401s wait on same promise
      if (!_refreshPromise) {
        _refreshPromise = tryRefreshToken().finally(() => { _refreshPromise = null; });
      }
      const refreshed = await _refreshPromise;
      if (refreshed === true) {
        onTokenRefresh?.();
        const newToken = getStoredTokens().accessToken;
        headers.Authorization = `Bearer ${newToken}`;
        res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
      } else {
        clearTokens();
        const msg = refreshed === "kicked"
          ? "Your account was signed in on another device."
          : "Session expired — please log in again";
        return { data: null, error: msg, fromMock: false };
      }
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail ?? `HTTP ${res.status}`);
    return { data, error: null, fromMock: false };
  } catch (err) {
    return { data: null, error: err.message, fromMock: false };
  }
}

// Distinct return values so callers can show the right message:
//   true          — refreshed successfully
//   false         — generic failure (expired, network error)
//   "kicked"      — server revoked this session because a new login elsewhere
//                   claimed the account
export async function tryRefreshToken() {
  const { refreshToken } = getStoredTokens();
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (res.status === 401) {
      const body = await res.json().catch(() => ({}));
      // Server sets this detail when a new login revoked this session
      if ((body.detail ?? "").includes("Session replaced")) return "kicked";
      return false;
    }
    const data = await res.json().catch(() => null);
    if (!data) return false;
    storeTokens(data);
    return true;
  } catch {
    return false;
  }
}

// ── Auth actions ──────────────────────────────────────────────────────────────
export async function authLogin(email, password) {
  const { data, error } = await rawFetch("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  if (error) return { error };
  storeTokens(data);
  return { data };
}

export async function authRegister(email, password) {
  const { data, error } = await rawFetch("/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  if (error) return { error };
  storeTokens(data);
  return { data };
}

export async function authLogout() {
  const { accessToken } = getStoredTokens();
  if (accessToken) {
    await apiFetch("/auth/logout", { method: "POST" }).catch(() => {});
  }
  clearTokens();
}

export async function authForgotPassword(email) {
  return rawFetch("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}
